import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { subjectActions } from "../slices/subject";
import { getErrorMessage } from "../../utils/errorUtils";
import { SubjectRequestDto } from "../../shared/dtos/subject/SubjectRequestDto";
import { SubjectResponseDto } from "../../shared/dtos/subject/SubjectResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createSubject =
  (values: SubjectRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(subjectActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<SubjectResponseDto>
      >("/subjects", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Subject created successfully!");
        dispatch(subjectActions.createSuccess(data.data));
        dispatch(
          subjectActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(subjectActions.setErrorCreate(message));
    }
  };

export const getSubjects =
  (search?: string) => async (dispatch: AppDispatch) => {
    dispatch(subjectActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<SubjectResponseDto[]>
      >("/subjects", {
        headers: { requiresAuth: true },
        params: search ? { search } : undefined,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(subjectActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(subjectActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getSubjectById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(subjectActions.setLoadingFetchOne());
  try {
    const { data } = await axiosInstance.get<
      ApiResponseDto<SubjectResponseDto>
    >(`/subjects/${id}`, { headers: { requiresAuth: true } });

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(subjectActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(subjectActions.setErrorFetchOne(getErrorMessage(error)));
  }
};

export const updateSubject =
  (payload: { id: string; values: SubjectRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(subjectActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<SubjectResponseDto>
      >(`/subjects/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Subject updated successfully!");
        dispatch(subjectActions.updateSuccess(data.data));
        dispatch(
          subjectActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(subjectActions.setErrorUpdate(message));
    }
  };

export const deleteSubject = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(subjectActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/subjects/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "Subject deleted successfully!");
      dispatch(subjectActions.deleteSuccess(id));
      dispatch(
        subjectActions.setServerStatus({
          operation: "delete",
          status: data.statusCode || 200,
        }),
      );
    }
  } catch (error) {
    const message = getErrorMessage(error);
    toast.error(message);
    dispatch(subjectActions.setErrorDelete(message));
  }
};

export const resetSubjectServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(subjectActions.setServerStatus(null));
};
